import { Component } from '@theme/component';

/**
 * SSC events index (sections/ssc-events.liquid).
 * - Reads the server-rendered rows ([data-event]) once: filter values live in data attributes.
 * - Re-groups upcoming / archive with the visitor's clock (the page may be cached), keeping
 *   upcoming ascending and the archive descending.
 * - Filter panel behind the "Show filters +" toggle, closed by default (aria-expanded mirrors it).
 * - Builds the filter options with counts, "Open [05]" (AVAILABILITY, EVENT TYPE, LOCATION,
 *   ARCHIVE year). Values and labels come from the rows' data attributes, so event types are
 *   whatever the metaobject entries use (nothing is hardcoded except the status order).
 *   OR within a group, AND across groups. Filters live in the URL (?city=berlin,munich&year=2026)
 *   and are kept in sync with history.replaceState.
 * - LIST | CALENDAR: month grid (Mon-Sun) from the same rows and filters, starting at the current
 *   month (or ?month=YYYY-MM). PageUp / PageDown or the arrow buttons change the month.
 *
 * @typedef {{
 *   el: HTMLElement, start: number, end: number, date: string, title: string, url: string,
 *   status: string, statusLabel: string, type: string, typeLabel: string,
 *   city: string, cityLabel: string, year: string, past: boolean
 * }} EventRow
 * @typedef {'status' | 'type' | 'city' | 'year'} Group
 * @typedef {{
 *   filterToggle: HTMLButtonElement, filterToggleLabel: HTMLElement, activeCount: HTMLElement,
 *   filters: HTMLElement, clear: HTMLButtonElement, done: HTMLButtonElement, live: HTMLElement,
 *   listView: HTMLElement, upcomingList: HTMLElement, pastList: HTMLElement, archive: HTMLElement,
 *   empty: HTMLElement, noResults: HTMLElement, viewSwitch?: HTMLElement, calendarView?: HTMLElement,
 *   prevMonth?: HTMLButtonElement, nextMonth?: HTMLButtonElement, monthLabel?: HTMLElement,
 *   weekdays?: HTMLElement, grid?: HTMLElement, monthEmpty?: HTMLElement
 * }} Refs
 * @extends {Component<Refs>}
 */
class SscEvents extends Component {
  requiredRefs = ['filterToggle', 'filters', 'live', 'upcomingList', 'pastList', 'archive'];

  /** @type {Group[]} */
  static GROUPS = ['status', 'type', 'city', 'year'];

  /** Availability options in a fixed, meaningful order */
  static STATUS_ORDER = ['open', 'waitlist', 'sold-out', 'closed', 'coming-soon'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {EventRow[]} */
  #events = [];

  /** @type {Record<Group, Set<string>>} */
  #selected = { status: new Set(), type: new Set(), city: new Set(), year: new Set() };

  /** @type {'list' | 'calendar'} */
  #view = 'list';

  /** First day of the shown calendar month */
  #month = new Date();

  connectedCallback() {
    super.connectedCallback();

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.#readEvents();
    this.#group();
    this.#buildOptions();
    this.#readUrl();

    this.refs.filterToggle.hidden = false;
    if (this.refs.viewSwitch) this.refs.viewSwitch.hidden = false;

    this.refs.filterToggle.addEventListener('click', () => this.#setFiltersOpen(this.refs.filters.hidden), { signal });
    this.refs.done?.addEventListener('click', () => this.#setFiltersOpen(false, true), { signal });
    this.refs.clear?.addEventListener('click', this.handleClear, { signal });
    this.refs.filters.addEventListener('change', this.handleFilterChange, { signal });
    this.refs.filters.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape') this.#setFiltersOpen(false, true);
      },
      { signal }
    );
    this.refs.viewSwitch?.addEventListener('click', this.handleViewClick, { signal });
    this.refs.prevMonth?.addEventListener('click', () => this.#stepMonth(-1), { signal });
    this.refs.nextMonth?.addEventListener('click', () => this.#stepMonth(1), { signal });
    this.refs.calendarView?.addEventListener('keydown', this.handleCalendarKeydown, { signal });

    this.#renderWeekdays();
    this.#apply({ announce: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  /** @param {Event} event */
  handleFilterChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;
    const group = /** @type {Group} */ (input.name);
    if (!SscEvents.GROUPS.includes(group)) return;
    if (input.checked) this.#selected[group].add(input.value);
    else this.#selected[group].delete(input.value);
    this.#apply();
  };

  handleClear = () => {
    for (const group of SscEvents.GROUPS) this.#selected[group].clear();
    for (const input of this.refs.filters.querySelectorAll('input[type="checkbox"]')) {
      if (input instanceof HTMLInputElement) input.checked = false;
    }
    this.#apply();
  };

  /** @param {MouseEvent} event */
  handleViewClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-view-button]') : null;
    if (!(button instanceof HTMLElement)) return;
    this.#setView(button.dataset.viewButton === 'calendar' ? 'calendar' : 'list');
    this.#writeUrl();
  };

  /** @param {KeyboardEvent} event */
  handleCalendarKeydown = (event) => {
    if (event.key === 'PageUp') {
      event.preventDefault();
      this.#stepMonth(-1);
    } else if (event.key === 'PageDown') {
      event.preventDefault();
      this.#stepMonth(1);
    }
  };

  #readEvents() {
    this.#events = [];
    for (const el of this.querySelectorAll('[data-event]')) {
      if (!(el instanceof HTMLElement)) continue;
      const data = el.dataset;
      const start = Number(data.start) || 0;
      this.#events.push({
        el,
        start,
        end: Number(data.end) || start,
        date: data.date || '',
        title: data.title || '',
        url: data.url || '',
        status: data.status || '',
        statusLabel: data.statusLabel || '',
        type: data.type || '',
        typeLabel: data.typeLabel || '',
        city: data.city || '',
        cityLabel: data.cityLabel || '',
        year: data.year || '',
        past: false,
      });
    }
  }

  /** Upcoming (ascending) vs archive (descending), by the visitor's clock */
  #group() {
    const now = Date.now() / 1000;
    const upcoming = [];
    const past = [];
    for (const event of this.#events) {
      event.past = event.end < now;
      (event.past ? past : upcoming).push(event);
    }
    upcoming.sort((a, b) => a.start - b.start);
    past.sort((a, b) => b.start - a.start);
    this.refs.upcomingList.append(...upcoming.map((event) => event.el));
    this.refs.pastList.append(...past.map((event) => event.el));
  }

  #buildOptions() {
    for (const group of SscEvents.GROUPS) {
      const fieldset = this.refs.filters.querySelector(`[data-group="${group}"]`);
      const container = fieldset?.querySelector('[data-options]');
      if (!(fieldset instanceof HTMLFieldSetElement) || !container) continue;

      /** @type {Map<string, {label: string, count: number}>} */
      const values = new Map();
      for (const event of this.#events) {
        const value = this.#value(event, group);
        if (!value) continue;
        const entry = values.get(value) || { label: this.#label(event, group), count: 0 };
        entry.count += 1;
        values.set(value, entry);
      }

      const keys = [...values.keys()];
      if (group === 'status') {
        const rank = (/** @type {string} */ key) => {
          const index = SscEvents.STATUS_ORDER.indexOf(key);
          return index === -1 ? 99 : index;
        };
        keys.sort((a, b) => rank(a) - rank(b));
      } else if (group === 'year') {
        keys.sort((a, b) => Number(b) - Number(a));
      } else {
        keys.sort((a, b) => (values.get(a)?.label || '').localeCompare(values.get(b)?.label || ''));
      }

      container.replaceChildren(
        ...keys.map((key) => {
          const { label, count } = /** @type {{label: string, count: number}} */ (values.get(key));
          const option = document.createElement('label');
          option.className = 'ssc-events__option';
          const input = document.createElement('input');
          input.type = 'checkbox';
          input.name = group;
          input.value = key;
          const text = document.createElement('span');
          text.textContent = label;
          const countText = document.createElement('span');
          countText.className = 'ssc-events__count';
          countText.textContent = `[${String(count).padStart(2, '0')}]`;
          option.append(input, text, countText);
          return option;
        })
      );
      fieldset.hidden = keys.length === 0;
    }
  }

  /**
   * @param {EventRow} event
   * @param {Group} group
   */
  #value(event, group) {
    return event[group];
  }

  /**
   * @param {EventRow} event
   * @param {Group} group
   */
  #label(event, group) {
    if (group === 'status') return event.statusLabel || event.status;
    if (group === 'type') return event.typeLabel || event.type;
    if (group === 'city') return event.cityLabel || event.city;
    return event.year;
  }

  #readUrl() {
    const params = new URLSearchParams(window.location.search);
    for (const group of SscEvents.GROUPS) {
      const raw = params.get(group);
      if (!raw) continue;
      for (const value of raw.split(',')) {
        const clean = value.trim().toLowerCase();
        if (!clean) continue;
        const input = this.refs.filters.querySelector(
          `input[name="${group}"][value="${CSS.escape(clean)}"]`
        );
        if (input instanceof HTMLInputElement) {
          input.checked = true;
          this.#selected[group].add(clean);
        }
      }
    }

    const month = params.get('month');
    const match = month ? /^(\d{4})-(\d{2})$/.exec(month) : null;
    const today = new Date();
    this.#month = match
      ? new Date(Number(match[1]), Number(match[2]) - 1, 1)
      : new Date(today.getFullYear(), today.getMonth(), 1);

    if (params.get('view') === 'calendar' && this.refs.calendarView) this.#setView('calendar');
  }

  #writeUrl() {
    const url = new URL(window.location.href);
    for (const group of SscEvents.GROUPS) {
      const values = [...this.#selected[group]];
      if (values.length) url.searchParams.set(group, values.join(','));
      else url.searchParams.delete(group);
    }
    if (this.#view === 'calendar') {
      url.searchParams.set('view', 'calendar');
      url.searchParams.set('month', this.#monthKey(this.#month));
    } else {
      url.searchParams.delete('view');
      url.searchParams.delete('month');
    }
    // Keep commas readable in shared links
    const search = url.searchParams.toString().replace(/%2C/gi, ',');
    const next = `${url.pathname}${search ? `?${search}` : ''}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      history.replaceState(history.state, '', next);
    }
  }

  /** @param {EventRow} event */
  #matches(event) {
    return SscEvents.GROUPS.every((group) => {
      const selected = this.#selected[group];
      return selected.size === 0 || selected.has(this.#value(event, group));
    });
  }

  #activeCount() {
    return SscEvents.GROUPS.reduce((sum, group) => sum + this.#selected[group].size, 0);
  }

  /** @param {{announce?: boolean}} [options] */
  #apply({ announce = true } = {}) {
    let upcoming = 0;
    let past = 0;
    for (const event of this.#events) {
      const match = this.#matches(event);
      event.el.hidden = !match;
      if (!match) continue;
      if (event.past) past += 1;
      else upcoming += 1;
    }

    const active = this.#activeCount();
    const total = upcoming + past;
    this.refs.archive.hidden = past === 0;
    if (this.refs.empty) this.refs.empty.hidden = upcoming > 0 || (active > 0 && total === 0);
    if (this.refs.noResults) this.refs.noResults.hidden = !(active > 0 && total === 0);
    this.refs.activeCount.textContent = active ? `[${String(active).padStart(2, '0')}]` : '';

    if (this.#view === 'calendar') this.#renderCalendar();
    this.#writeUrl();
    if (announce) this.refs.live.textContent = this.#countText(total);
  }

  /** @param {number} count */
  #countText(count) {
    if (count === 0) return this.dataset.countNone || '';
    const template = (count === 1 ? this.dataset.countOne : this.dataset.countOther) || '';
    return template.replace('[count]', String(count));
  }

  /**
   * @param {boolean} open
   * @param {boolean} [returnFocus]
   */
  #setFiltersOpen(open, returnFocus = false) {
    this.refs.filters.hidden = !open;
    this.refs.filterToggle.setAttribute('aria-expanded', String(open));
    if (this.refs.filterToggleLabel) {
      this.refs.filterToggleLabel.textContent = (open ? this.dataset.labelHide : this.dataset.labelShow) || '';
    }
    if (!open && returnFocus) this.refs.filterToggle.focus();
  }

  /** @param {'list' | 'calendar'} view */
  #setView(view) {
    if (!this.refs.calendarView) view = 'list';
    this.#view = view;
    this.dataset.view = view;
    this.refs.listView.hidden = view !== 'list';
    if (this.refs.calendarView) this.refs.calendarView.hidden = view !== 'calendar';
    for (const button of this.querySelectorAll('[data-view-button]')) {
      if (button instanceof HTMLElement) {
        button.setAttribute('aria-pressed', String(button.dataset.viewButton === view));
      }
    }
    if (view === 'calendar') this.#renderCalendar();
  }

  /** @param {number} step */
  #stepMonth(step) {
    this.#month = new Date(this.#month.getFullYear(), this.#month.getMonth() + step, 1);
    this.#renderCalendar();
    this.#writeUrl();
  }

  /** @param {Date} date */
  #monthKey(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  }

  /** @param {Date} date */
  #dayKey(date) {
    return `${this.#monthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;
  }

  get #locale() {
    return document.documentElement.lang || undefined;
  }

  #renderWeekdays() {
    const { weekdays } = this.refs;
    if (!weekdays) return;
    const format = new Intl.DateTimeFormat(this.#locale, { weekday: 'short' });
    // 2024-01-01 was a Monday
    weekdays.replaceChildren(
      ...Array.from({ length: 7 }, (_, index) => {
        const span = document.createElement('span');
        span.textContent = format.format(new Date(2024, 0, 1 + index)).replace('.', '');
        return span;
      })
    );
  }

  #renderCalendar() {
    const { grid, monthLabel, monthEmpty } = this.refs;
    if (!grid || !monthLabel) return;

    const month = this.#month;
    monthLabel.textContent = new Intl.DateTimeFormat(this.#locale, { month: 'long', year: 'numeric' }).format(month);

    /** @type {Map<string, EventRow[]>} */
    const byDay = new Map();
    for (const event of this.#events) {
      if (!event.date || !this.#matches(event)) continue;
      const list = byDay.get(event.date) || [];
      list.push(event);
      byDay.set(event.date, list);
    }
    for (const list of byDay.values()) list.sort((a, b) => a.start - b.start);

    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const offset = (first.getDay() + 6) % 7; // Monday first
    const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = Math.ceil((offset + daysInMonth) / 7) * 7;
    const todayKey = this.#dayKey(new Date());
    const dowFormat = new Intl.DateTimeFormat(this.#locale, { weekday: 'short' });
    const fullFormat = new Intl.DateTimeFormat(this.#locale, { weekday: 'long', day: 'numeric', month: 'long' });

    let eventsInMonth = 0;
    const items = [];
    for (let index = 0; index < cells; index++) {
      const date = new Date(month.getFullYear(), month.getMonth(), 1 - offset + index);
      const key = this.#dayKey(date);
      const outside = date.getMonth() !== month.getMonth();
      const events = outside ? [] : byDay.get(key) || [];
      eventsInMonth += events.length;

      const item = document.createElement('li');
      item.className = 'ssc-cal__day';
      item.classList.toggle('is-outside', outside);
      item.classList.toggle('has-events', events.length > 0);
      item.classList.toggle('is-today', key === todayKey);
      if (outside) item.setAttribute('aria-hidden', 'true');

      const number = document.createElement('span');
      number.className = 'ssc-cal__num';
      const dow = document.createElement('span');
      dow.className = 'ssc-cal__dow';
      dow.setAttribute('aria-hidden', 'true');
      dow.textContent = dowFormat.format(date).replace('.', '');
      const day = document.createElement('span');
      day.setAttribute('aria-hidden', 'true');
      day.textContent = String(date.getDate()).padStart(2, '0');
      const label = document.createElement('span');
      label.className = 'visually-hidden';
      label.textContent = fullFormat.format(date) + (key === todayKey ? `, ${this.dataset.today || ''}` : '');
      number.append(dow, day, label);
      item.append(number);

      if (events.length) {
        const list = document.createElement('ul');
        list.className = 'ssc-cal__events';
        list.setAttribute('role', 'list');
        for (const event of events) {
          const li = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'ssc-cal__event';
          link.classList.toggle('is-past', event.past);
          link.href = event.url;
          link.textContent = event.cityLabel ? `${event.title} | ${event.cityLabel}` : event.title;
          link.title = link.textContent;
          li.append(link);
          list.append(li);
        }
        item.append(list);
      }
      items.push(item);
    }

    grid.replaceChildren(...items);
    if (monthEmpty) monthEmpty.hidden = eventsInMonth > 0;
  }
}

if (!customElements.get('ssc-events')) customElements.define('ssc-events', SscEvents);
